import type { Actor, Currency, VehicleRecord, VehicleType } from './contracts.ts';

export const VEHICLE_PUBLISHING_OUTLETS = ['facebook-page', 'instagram', 'facebook-dealer-inventory', 'kijiji-dealer-inventory'] as const;
export type VehiclePublishingOutlet = typeof VEHICLE_PUBLISHING_OUTLETS[number];
export type VehiclePublishingWrite = { actor: Actor; key: string; expectedVersion: number };
export type VehiclePublishingSelectionInput = {
  vehicleId: string; outlet: VehiclePublishingOutlet; enabled: boolean; paused: boolean;
  expectedVehicleVersion: number;
  price: { amountMinor: number; currency: Currency; source: 'retail' | 'approved' };
  description: string; contact: { name: string; phone: string; email: string };
  mediaAssetIds: string[]; coverAssetId: string; includeIdentifier: boolean;
  vehiclePageUrl: string | null;
};
export type VehiclePublishingSelection = VehiclePublishingSelectionInput & {
  id: string; version: number; approvedSnapshotId: string | null; approvedSelectionVersion: number | null;
  createdAt: string; updatedAt: string; actorId: string;
};
/** Native public content, NOT an asserted provider feed/API schema. No asset ids/costs/provenance. */
export type VehiclePublishingPayload = {
  category: VehicleType; title: string; year: number | null; make: string; model: string; trim: string;
  odometerKm: number | null; exteriorColour: string; drivetrain: string; transmission: string; fuel: string;
  identifier: { kind: 'VIN' | 'HIN' | 'Serial'; value: string } | null;
  price: { amountMinor: number; currency: Currency }; description: string;
  contact: { name: string; phone: string; email: string }; vehiclePageUrl: string | null;
  photos: { url: string | null; mimeType: string; cover: boolean }[];
};
export type VehiclePublishingSnapshot = {
  id: string; selectionId: string; outlet: VehiclePublishingOutlet; vehicleId: string;
  vehicleVersion: number; sourceHash: string; payloadHash: string; payload: VehiclePublishingPayload;
  /** Internal proof only. Never send to an outlet. */
  source: { availability: VehicleRecord['availability']; media: { assetId: string; sha256: string; size: number; mimeType: string }[] };
  ready: boolean; reasons: string[];
};
export interface VehiclePublishingProjectionGateway {
  preview(selectionId: string, input: VehiclePublishingSelectionInput, actor: Actor): VehiclePublishingSnapshot;
  readAvailability(vehicleId: string, actor: Actor): { version: number; availability: VehicleRecord['availability'] };
}
/** Root-only verified delivery configuration; no URL fetch or readiness discovery is done by this module. */
export type VehiclePublishingDelivery = { origin: string; verifiedAt: string; sourceReference: string; vehicleUrl: (vehicleId: string) => string; mediaUrl: (vehicleId: string, assetId: string) => string };
export type VehiclePublishingRuleInput = { enabled: boolean; automaticCreate: boolean; automaticUpdate: boolean; automaticWithdraw: boolean; delayMinutes: number };
export type VehiclePublishingRule = VehiclePublishingRuleInput & { outlet: VehiclePublishingOutlet; version: number; updatedAt: string; actorId: string };
export type VehiclePublishingControls = { paused: boolean; version: number; updatedAt: string | null; actorId: string | null };
export type VehiclePublishingAction = 'create' | 'update' | 'withdraw';
export type VehiclePublishingOperation = {
  id: string; selectionId: string; outlet: VehiclePublishingOutlet; vehicleId: string;
  action: VehiclePublishingAction; snapshotId: string | null; providerKey: string;
  status: 'queued' | 'sending' | 'uncertain' | 'retry-wait' | 'succeeded' | 'failed' | 'cancelled' | 'superseded';
  version: number; attempts: number; nextAttemptAt: string; activeAttemptId: string | null;
  lastError: string | null; createdAt: string; updatedAt: string; actorId: string;
};
export type VehiclePublishingReceipt = {
  id: string; operationId: string; attemptId: string;
  kind: 'uploaded' | 'feed-delivered' | 'live' | 'updated' | 'removed' | 'not-found' | 'rejected' | 'unknown';
  receiptId: string | null; listingId: string | null; listingUrl: string | null;
  checkedAt: string; message: string | null;
};
export type VehiclePublishingProviderResult = Omit<VehiclePublishingReceipt, 'id' | 'operationId' | 'attemptId'>;
export type VehiclePublishingCapability = {
  outlet: VehiclePublishingOutlet; enabled: boolean; verified: boolean;
  verifiedAt: string | null; sourceReference: string | null; accountReference: string | null;
  create: boolean; update: boolean; withdraw: boolean; reconcile: boolean;
  categoryMappings: { category: VehicleType; providerCategory: string; sourceReference: string }[];
  mediaMimeTypes: string[];
};
export type VehiclePublishingProviderRequest = {
  operationId: string; providerKey: string; outlet: VehiclePublishingOutlet; action: VehiclePublishingAction;
  /** Closed public payload only; internal snapshot source is NEVER passed to executor. */
  payload: VehiclePublishingPayload | null; listingId: string | null;
};
export interface VehiclePublishingProvider {
  capability(outlet: VehiclePublishingOutlet): VehiclePublishingCapability;
  execute(request: VehiclePublishingProviderRequest): Promise<VehiclePublishingProviderResult>;
  reconcile(request: VehiclePublishingProviderRequest): Promise<VehiclePublishingProviderResult>;
}
export type VehiclePublishingConnection = { outlet: VehiclePublishingOutlet; ready: boolean; reason: string; lastCheckedAt: string | null };
export type VehiclePublishingStatus = {
  selectionId: string; vehicleId: string; outlet: VehiclePublishingOutlet;
  status: 'Not connected' | 'Ready' | 'Scheduled' | 'Sending' | 'Posted/Listed' | 'Needs attention' | 'Update pending' | 'Removal pending' | 'Removed';
  listingId: string | null; listingUrl: string | null; lastCheckedAt: string | null; reason: string | null;
};
export type VehiclePublishingOperationDetail = { operation: VehiclePublishingOperation; snapshot: VehiclePublishingSnapshot | null; receipts: VehiclePublishingReceipt[] };
export interface VehiclePublishingGateway {
  saveSelection(id: string, input: VehiclePublishingSelectionInput, write: VehiclePublishingWrite): VehiclePublishingSelection;
  getSelection(id: string, actor: Actor): VehiclePublishingSelection;
  listSelections(actor: Actor): VehiclePublishingSelection[];
  previewSelection(id: string, actor: Actor): VehiclePublishingSnapshot;
  approveSelection(id: string, expectedSourceHash: string, write: VehiclePublishingWrite): VehiclePublishingSelection;
  saveRule(outlet: VehiclePublishingOutlet, input: VehiclePublishingRuleInput, write: VehiclePublishingWrite): VehiclePublishingRule;
  listRules(actor: Actor): VehiclePublishingRule[];
  saveControls(input: { paused: boolean }, write: VehiclePublishingWrite): VehiclePublishingControls;
  getControls(actor: Actor): VehiclePublishingControls;
  connections(actor: Actor): VehiclePublishingConnection[];
  queueOperation(id: string, selectionId: string, action: VehiclePublishingAction, write: VehiclePublishingWrite): VehiclePublishingOperationDetail;
  /** Local automatic-rule planning only. Never starts provider I/O or a scheduler. */
  planSelection(id: string, write: VehiclePublishingWrite): VehiclePublishingOperationDetail | null;
  getOperation(id: string, actor: Actor): VehiclePublishingOperationDetail;
  listOperations(actor: Actor): VehiclePublishingOperationDetail[];
  retryOperation(id: string, write: VehiclePublishingWrite): VehiclePublishingOperationDetail;
  cancelOperation(id: string, write: VehiclePublishingWrite): VehiclePublishingOperationDetail;
  /** Disabled unless explicitly injected verified capability. No built-in provider implementation. */
  runOperation(id: string, write: VehiclePublishingWrite): Promise<VehiclePublishingOperationDetail>;
  status(selectionId: string, actor: Actor): VehiclePublishingStatus;
}
